import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ICountry } from '@shared/interfaces/country.interface';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { Country } from '@shared/models/country.model';
import { SessionStorage } from 'ngx-webstorage';
import { Select2Option, Select2Data } from 'ng-select2-component';

@Component({
    selector: 'app-phone-number',
    templateUrl: './phone-number.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PhoneNumberComponent implements OnInit, OnChanges {
  @SessionStorage() public countries: ICountry[];

  // Callers commonly bind FormArray items / entry.get(...) results, which are
  // typed AbstractControl even though the runtime object is a FormGroup.
  @Input() public formGroup: AbstractControl;
  @Input() public language = String('en');
  @Input() public explainer = String('');

  @SessionStorage() private hiringFirm: IHiringFirm;

  public mask = '00000000000000000000';

  public get fg(): FormGroup {
    return this.formGroup as FormGroup;
  }
  public countriesOptions: Array<Select2Option> = [];

  private defaultCountry: ICountry;

  constructor() {}

  ngOnInit(): void {
    this.initializeCountriesOptions();

    this.defaultCountry =
      this.countries?.find((c: ICountry) => c.initial === 'US') ||
      new Country({
        initial: 'US',
        phoneCode: '1'
      });

    this.initializeCountryCode();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reinitialize country code when formGroup changes (e.g., form reset)
    if (changes.formGroup && !changes.formGroup.firstChange) {
      this.initializeCountryCode();
    }
  }

  private initializeCountryCode(): void {
    // Use setTimeout to ensure form controls are initialized
    setTimeout(() => {
      // Check if there's already a countryInitial value set (e.g., from editing a reference)
      const existingCountryInitial = this.formGroup?.get('countryInitial')?.value;

      if (existingCountryInitial) {
        // Value already set, don't override it
        return;
      }

      if (!!!this.hiringFirm || !!!this.countries) {
        return this.setCountryCode(this.defaultCountry);
      }

      // Map CA to US since they share the same country code
      const countryInitial = this.hiringFirm.country === 'CA' ? 'US' : (this.hiringFirm.country || 'US');

      const country: ICountry =
        this.countries.find(
          (c: ICountry) => countryInitial === c.initial
        ) || this.defaultCountry;

      return this.setCountryCode(country);
    }, 0);
  }

  public updateCountryCode() {
    const countryCode = this.formGroup.value.countryInitial;

    let country = this.countries.find(
      (c: ICountry) => c.initial === countryCode
    );

    if (!!!country) {
      country = this.defaultCountry;
    }

    return this.formGroup.patchValue({
      country_code: country.phoneCode
    });
  }

  private initializeCountriesOptions(): void {
    this.countriesOptions = this.countries?.map((c: ICountry): Select2Option => ({
      value: c?.initial,
      label: `(+${c?.phoneCode}) ${c?.label[this.language]}`
    }));
  }

  private setCountryCode(c: ICountry): void {
    // Check if form controls exist before patching
    if (this.formGroup && this.formGroup.get('countryInitial') && this.formGroup.get('country_code')) {
      return this.formGroup.patchValue({
        country_code: c.phoneCode,
        countryInitial: c.initial
      });
    }
  }
}
